import { apiRequest } from '../../api/client';
import type { FiscalEnvironmentValue } from '../../invoices/api';
import type { TaxCondition } from '../../customers/api';

export type ArcaCertificateEnvironment = 'homologacion' | 'produccion';
export type CertificateStatus = 'missing' | 'active' | 'expiring-soon' | 'expired';

/** Matches `apps/api` `FiscalIssuerView` (`fiscal-config-reader.ts`). */
export interface FiscalIssuer {
  cuit: string;
  legalName: string;
  address: { street: string; city: string; province: string; postalCode: string };
  taxCondition: TaxCondition;
  grossIncomeNumber: string;
  activityStartDate: string;
}

/** Matches `apps/api` `FiscalPublicCertificateView`. */
export interface FiscalCertificate {
  environment: ArcaCertificateEnvironment;
  alias: string;
  subject: string | null;
  cuit: string | null;
  notBefore: string | null;
  notAfter: string | null;
  status: string;
}

/** Matches `apps/api` `FiscalFullConfig` (`GET /fiscal-config`). */
export interface FiscalConfig {
  issuer: FiscalIssuer | null;
  environment: FiscalEnvironmentValue;
  certificates: {
    homologacion: FiscalCertificate | null;
    produccion: FiscalCertificate | null;
  };
  checklist: {
    issuerDataComplete: boolean;
    certificateStatus: Record<ArcaCertificateEnvironment, CertificateStatus>;
    lastConnectionTest: Record<ArcaCertificateEnvironment, { at: string | null; success: boolean | null }>;
  };
}

/** Matches `apps/api` `GET /fiscal-config`. */
export function getFiscalConfig(): Promise<FiscalConfig> {
  return apiRequest<FiscalConfig>('/fiscal-config');
}

export interface UpdateFiscalIssuerInput {
  cuit: string;
  legalName: string;
  address: { street: string; city: string; province: string; postalCode: string };
  taxCondition: TaxCondition;
  grossIncomeNumber: string;
  activityStartDate: string;
}

/** Matches `apps/api` `PATCH /fiscal-config/issuer`. */
export function updateFiscalIssuer(input: UpdateFiscalIssuerInput): Promise<FiscalConfig> {
  return apiRequest<FiscalConfig>('/fiscal-config/issuer', {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

export interface GeneratedCsr {
  csrPem: string;
}

/** Matches `apps/api` `POST /fiscal-config/certificates/:environment/csr`. */
export function generateFiscalCsr(
  environment: ArcaCertificateEnvironment,
  alias: string,
): Promise<GeneratedCsr> {
  return apiRequest<GeneratedCsr>(`/fiscal-config/certificates/${environment}/csr`, {
    method: 'POST',
    body: JSON.stringify({ alias }),
  });
}

/** Matches `apps/api` `POST /fiscal-config/certificates/:environment/upload`. `certificatePem` is the `.crt` file read as text, never the raw binary. */
export function uploadFiscalCertificate(
  environment: ArcaCertificateEnvironment,
  certificatePem: string,
): Promise<FiscalConfig> {
  return apiRequest<FiscalConfig>(`/fiscal-config/certificates/${environment}/upload`, {
    method: 'POST',
    body: JSON.stringify({ certificatePem }),
  });
}

export interface ConnectionCheckResult {
  ok: boolean | null;
  detail: string;
}

/** Matches `apps/api` `ConnectionTestResult` (`test-arca-connection.use-case.ts`). */
export interface ConnectionTestResult {
  wsaa: ConnectionCheckResult;
  dummy: ConnectionCheckResult;
  ptosVenta: ConnectionCheckResult;
  tiposIva: ConnectionCheckResult;
  success: boolean;
}

/** Matches `apps/api` `POST /fiscal-config/test-connection/:environment`. */
export function testArcaConnection(environment: ArcaCertificateEnvironment): Promise<ConnectionTestResult> {
  return apiRequest<ConnectionTestResult>(`/fiscal-config/test-connection/${environment}`, {
    method: 'POST',
  });
}

/** Matches `apps/api` `PATCH /fiscal-config/environment`. `cuitConfirmation` is required only toward `'produccion'`. */
export function updateFiscalEnvironment(
  environment: FiscalEnvironmentValue,
  cuitConfirmation?: string,
): Promise<FiscalConfig> {
  return apiRequest<FiscalConfig>('/fiscal-config/environment', {
    method: 'PATCH',
    body: JSON.stringify({ environment, ...(cuitConfirmation === undefined ? {} : { cuitConfirmation }) }),
  });
}
